import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type Question,
  type ModuleId,
  getQuestionTypeConfig,
  getAllQuestions,
  getAllowedTiersForGroup,
  MODULES,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { getAdaptiveSessionV11 } from '../../mastery'
import { V11_CONFIG } from '../../mastery'
import type { AppState } from '../../app/types'
import type { GroupId, StartingPeriod } from '../../config/systemConfig'
import { SESSION_CONFIG, calculateXP } from '../../config/constants'
import { debug, warn, error as logError } from '../../utils/logger'
import { playSoundIntent } from '../../utils/sound/soundIntent'
import { createMasteryQueries } from '../../accounts/hooks/useLearnerActions'
import { isFastResponse } from '../logic/questionTimeBaselines'
import { getTodayChallenge } from '../../utils/dailyChallenge'
import { filterSession, evaluateQuestion } from '../logic/pedagogicalGuard'
import type { FeedbackOrchestration } from './useFeedbackOrchestration'

/**
 * useQuestionFlow — the core question lifecycle for a practice session.
 *
 * Extracted from PracticeSession.tsx as part of the F-FE-1 god-component
 * split (K12: custom hooks, not mini-components). This is the largest of
 * the four hooks. It owns:
 *   • Session initialization (adaptive engine v11.2 + pedagogical guard +
 *     preFamilyMastery clone + initError).
 *   • The session buffer (`sessionQuestions`), `currentQuestionIndex`,
 *     `phase`, `sessionInitialized`.
 *   • The memoized `moduleEligiblePool` shared by the P-12 / P-13 swap
 *     paths (F-PE-2 / F-PE-3 mitigation — PRESERVED).
 *   • Answer-input state and the answer pipeline (`processAnswer`, incl.
 *     P-12 frustration swap, P-13 mid-session difficulty adjust, daily-
 *     challenge progress, speed streak, XP), plus submit / next / reset.
 *   • The Enter-key window handler (F-QA-4 focus guard — PRESERVED).
 *   • The skip flow ("Ik weet het echt niet").
 *
 * Presentation state (streak / XP / motivational copy / nudge) lives in
 * useFeedbackOrchestration; this hook receives that API and drives its
 * setters rather than duplicating the state.
 *
 * HOOKS-ORDER SAFETY: called unconditionally at the top of PracticeSession.
 * Every function here is defined via useCallback and returned, so the hook
 * count is stable regardless of which full-page early-return the caller
 * subsequently hits (avoids React #310).
 */
export type Phase =
  | 'question'
  | 'feedback'
  | 'summary'
  | 'fluency-correct'
  | 'fluency-incorrect'

export interface SessionAnswer {
  questionId: string
  isCorrect: boolean
  familyId?: string
  questionType: string
  errorType?: string
  timeMs?: number
}

const MAX_SKIPS_PER_SESSION = 2
const SKIP_FLASH_DURATION_MS = 1800

function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false
  return getQuestionTypeConfig(question.questionType || 'multiple_choice')
    .isInteractive
}

function getResetState() {
  return {
    currentQuestionIndex: 0,
    phase: 'question' as Phase,
    selectedOption: null as number | null,
    userAnswerIndex: null as number | null,
    userTypedAnswer: '',
    actualIsCorrect: null as boolean | null,
    interactiveReady: false,
    shouldValidateInteractive: false,
  }
}

interface UseQuestionFlowParams {
  moduleId: ModuleId | null
  questions: Question[]
  isCrossModule: boolean
  isFluency: boolean
  isChallenge: boolean
  showCheatSheet: boolean
  labMode?: string
  pendingLevelUp?: number | null
  onClearPendingLevelUp?: () => void
  // App context (from useAppStore, resolved in the orchestrator)
  state: AppState
  selectedGroup: GroupId
  groupVersion: number
  isInitialized: boolean
  manualPeriod: StartingPeriod | null
  updateDailyChallengeProgress: (
    amount: number,
    type: string,
    targetCount: number,
  ) => void
  onAnswer: (
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
    timeMs?: number,
    helpUsed?: boolean,
    skipped?: boolean,
  ) => void
  // Feedback presentation API
  feedback: FeedbackOrchestration
  // Cross-hook helpers
  isCheatSheetOpen: boolean
  /** Called when the visible question changes (e.g. to close the scratchpad). */
  onQuestionChange?: () => void
  /** Motivational-copy resolver (from utils/motivationalMessages). */
  motivational: (args: {
    isCorrect: boolean
    streak: number
    questionNumber: number
    totalQuestions: number
  }) => string
  streakMessage: (streak: number) => string | null
}

export interface QuestionFlow {
  // Session state
  sessionQuestions: Question[]
  currentQuestionIndex: number
  currentQuestion: Question | undefined
  isLastQuestion: boolean
  phase: Phase
  sessionInitialized: boolean
  initError: string | null
  preFamilyMastery: Record<string, FamilyMasteryState>
  sessionAnswers: SessionAnswer[]
  // Answer input state
  selectedOption: number | null
  userAnswerIndex: number | null
  userTypedAnswer: string
  actualIsCorrect: boolean | null
  interactiveReady: boolean
  shouldValidateInteractive: boolean
  setInteractiveReady: React.Dispatch<React.SetStateAction<boolean>>
  setPhase: React.Dispatch<React.SetStateAction<Phase>>
  // Challenge adaptivity
  challengeTierDowngraded: boolean
  // Help tracking
  helpUsedForCurrentQuestionRef: React.MutableRefObject<boolean>
  /** Timestamp the current question was shown — shared with useSessionTimer. */
  questionStartTimeRef: React.MutableRefObject<number>
  // Handlers
  handleRendererAnswer: (userAnswer: string | number) => void
  submitAnswer: () => void
  nextQuestion: () => void
  resetSession: () => void
  // Skip flow
  skipCount: number
  showSkipConfirm: boolean
  setShowSkipConfirm: React.Dispatch<React.SetStateAction<boolean>>
  cheatSheetEverOpened: boolean
  setCheatSheetEverOpened: React.Dispatch<React.SetStateAction<boolean>>
  /**
   * Skip eligibility EXCLUDING the 20 s time gate (which lives in
   * useSessionTimer). The orchestrator ANDs this with `canSkipTimePassed`
   * to produce the final "Ik weet het echt niet" visibility.
   */
  canShowSkipBase: boolean
  skipFlashVisible: boolean
  /** Show the skip-confirm sheet; caller passes the fully-resolved gate. */
  requestSkip: (eligible: boolean) => void
  handleSkipConfirm: () => void
  handleRendererFailure: () => void
  isInteractiveQuestion: (question?: Question) => boolean
}

export function useQuestionFlow(params: UseQuestionFlowParams): QuestionFlow {
  const {
    moduleId,
    questions,
    isCrossModule,
    isFluency,
    isChallenge,
    showCheatSheet,
    pendingLevelUp = null,
    onClearPendingLevelUp,
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod,
    updateDailyChallengeProgress,
    onAnswer,
    feedback,
    isCheatSheetOpen,
    onQuestionChange,
    motivational,
    streakMessage,
  } = params

  // ── Session state ──────────────────────────────────────────
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('question')
  const [sessionInitialized, setSessionInitialized] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const lastGroupVersionRef = useRef(groupVersion)

  // ── Answer input state ─────────────────────────────────────
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null)
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('')
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null)
  const [interactiveReady, setInteractiveReady] = useState(false)
  const [shouldValidateInteractive, setShouldValidateInteractive] =
    useState(false)

  // ── Skip feature state ─────────────────────────────────────
  const [skipCount, setSkipCount] = useState(0)
  const [showSkipConfirm, setShowSkipConfirm] = useState(false)
  const [cheatSheetEverOpened, setCheatSheetEverOpened] = useState(false)
  const [skipFlashVisible, setSkipFlashVisible] = useState(false)
  const skipFlashTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // ── Session tracking ───────────────────────────────────────
  const [sessionAnswers, setSessionAnswers] = useState<SessionAnswer[]>([])
  const [preFamilyMastery, setPreFamilyMastery] = useState<
    Record<string, FamilyMasteryState>
  >({})
  const questionStartTimeRef = useRef<number>(Date.now())
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const answerProcessedRef = useRef(false)
  const helpUsedForCurrentQuestionRef = useRef(false)
  const submitAnswerRef = useRef<() => void>(() => {})

  // ── Challenge adaptivity ───────────────────────────────────
  const challengeConsecutiveErrorsRef = useRef(0)
  const [challengeTierDowngraded, setChallengeTierDowngraded] = useState(false)
  const challengeTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // ── P-12 / P-13 refs ───────────────────────────────────────
  const familyErrorStreaksRef = useRef<Map<string, number>>(new Map())
  const lastDifficultyAdjustIndexRef = useRef<number>(-999)

  // ── Reset helper ───────────────────────────────────────────
  const resetSession = useCallback(() => {
    const r = getResetState()
    setSessionQuestions([])
    setSessionInitialized(false)
    setCurrentQuestionIndex(r.currentQuestionIndex)
    setPhase(r.phase)
    setSelectedOption(r.selectedOption)
    setUserAnswerIndex(r.userAnswerIndex)
    setUserTypedAnswer(r.userTypedAnswer)
    setActualIsCorrect(r.actualIsCorrect)
    setInteractiveReady(r.interactiveReady)
    setShouldValidateInteractive(r.shouldValidateInteractive)
    setSessionAnswers([])
    feedback.resetFeedback()
    challengeConsecutiveErrorsRef.current = 0
    familyErrorStreaksRef.current.clear()
    setChallengeTierDowngraded(false)
    setSkipCount(0)
    setCheatSheetEverOpened(false)
    setShowSkipConfirm(false)
    setSkipFlashVisible(false)
    clearTimeout(autoAdvanceTimerRef.current)
    clearTimeout(challengeTimerRef.current)
    clearTimeout(skipFlashTimerRef.current)
    answerProcessedRef.current = false
  }, [feedback])

  // ── Cleanup timers on unmount ──────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(autoAdvanceTimerRef.current)
      clearTimeout(challengeTimerRef.current)
      clearTimeout(skipFlashTimerRef.current)
    }
  }, [])

  // ── Session initialization (adaptive engine) ───────────────
  useEffect(() => {
    if (!isInitialized) return
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion
      resetSession()
      debug('Session fully reset due to group change')
    }
    if (!sessionInitialized) {
      setInitError(null)
      if (state.familyMastery) {
        try {
          setPreFamilyMastery(structuredClone(state.familyMastery))
        } catch (err) {
          logError('Failed to clone familyMastery:', err)
          setPreFamilyMastery({})
        }
      }
      if (isCrossModule) {
        setSessionQuestions(questions)
        setSessionInitialized(true)
      } else if (moduleId) {
        try {
          const familyMastery = state.familyMastery ?? {}
          const questionStates = state.questionStates ?? {}
          const recentFamilies = Object.values(familyMastery)
            .filter((fm) => fm && (fm.lastSeen ?? 0) > 0)
            .sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0))
            .slice(0, 5)
            .map((fm) => fm.familyId)
            .filter((id) => id && id.length > 0)
          const recentQuestions = Object.entries(questionStates)
            .filter(([_, qs]) => qs && (qs.lastAttemptDate ?? 0) > 0)
            .sort(
              (a, b) =>
                (b[1]?.lastAttemptDate ?? 0) - (a[1]?.lastAttemptDate ?? 0),
            )
            .slice(0, 10)
            .map(([qId]) => qId.replace(/_g\d+$/, ''))
            .filter((qId) => qId && qId.length > 0)
          debug(
            `v11.2: Generating session for ${moduleId} | Group ${selectedGroup} | Families tracked: ${Object.keys(familyMastery).length}`,
          )
          const queries = createMasteryQueries(
            state,
            selectedGroup,
            manualPeriod,
          )
          const startingTier = queries.getStartingLevel(moduleId, selectedGroup)
          const adaptiveSession = getAdaptiveSessionV11(
            moduleId,
            familyMastery,
            selectedGroup,
            questionStates,
            SESSION_CONFIG.QUESTIONS_PER_SESSION,
            recentFamilies,
            recentQuestions,
            undefined,
            startingTier,
            undefined,
            state.inferredMastery,
          )
          if (adaptiveSession.length === 0) {
            warn(
              'Empty session generated - all content mastered or unavailable',
            )
            setInitError('empty_session')
          }
          // ── Pedagogical Guard (PEDAGOGICAL_CONTRACT_SPEC §5) ──
          const allModuleQuestions = getAllQuestions().filter(
            (q) => q.module === moduleId,
          )
          const { questions: guardedQuestions, stats: guardStats } =
            filterSession(
              adaptiveSession,
              SESSION_CONFIG.QUESTIONS_PER_SESSION,
              allModuleQuestions,
            )
          if (guardStats.fatalRejected > 0 || guardStats.softFailed > 0) {
            debug(
              `[PedagogicalGuard] Session filtered: ${guardStats.passed} passed, ${guardStats.softFailed} soft, ${guardStats.fatalRejected} fatal (${guardStats.replaced} replaced)`,
            )
          }
          setSessionQuestions(guardedQuestions)
          setSessionInitialized(true)
          debug(
            `Session: ${guardedQuestions.length} questions for ${moduleId} (group ${selectedGroup}, engine: v11.2, guard: ${guardStats.fatalRejected}F/${guardStats.softFailed}S)`,
          )
        } catch (error) {
          logError('Failed to generate session:', error)
          setInitError('generation_failed')
          setSessionQuestions([])
          setSessionInitialized(true)
        }
      }
    }
  }, [
    moduleId,
    isCrossModule,
    questions,
    sessionInitialized,
    selectedGroup,
    groupVersion,
    isInitialized,
    state.familyMastery,
    state.questionStates,
    manualPeriod,
    resetSession,
    state,
  ])

  const currentQuestion = sessionQuestions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1

  // ── Memoised candidate pool (F-PE-2 / F-PE-3, PRESERVED) ────
  const poolModuleId = moduleId ?? currentQuestion?.module ?? null
  const moduleEligiblePool = useMemo(() => {
    if (!poolModuleId) return [] as Question[]
    const allowedTiers = getAllowedTiersForGroup(selectedGroup)
    return getAllQuestions().filter(
      (q) =>
        q.module === poolModuleId &&
        allowedTiers.has(q.tier) &&
        evaluateQuestion(q as Question).ok,
    )
  }, [poolModuleId, selectedGroup])

  // ── Reset answer state when question changes ───────────────
  useEffect(() => {
    if (!currentQuestion) return
    setSelectedOption(null)
    setUserAnswerIndex(null)
    setUserTypedAnswer('')
    setInteractiveReady(false)
    setShouldValidateInteractive(false)
    questionStartTimeRef.current = Date.now()
    answerProcessedRef.current = false
    helpUsedForCurrentQuestionRef.current = false
    onQuestionChange?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion])

  // ── nextQuestion ───────────────────────────────────────────
  const nextQuestion = useCallback(() => {
    clearTimeout(autoAdvanceTimerRef.current)
    if (onClearPendingLevelUp && pendingLevelUp) onClearPendingLevelUp()
    if (currentQuestionIndex === sessionQuestions.length - 1) {
      setPhase('summary')
    } else {
      playSoundIntent('nav.move')
      setCurrentQuestionIndex((prev) => prev + 1)
      setSelectedOption(null)
      setUserAnswerIndex(null)
      setUserTypedAnswer('')
      setActualIsCorrect(null)
      setInteractiveReady(false)
      setShouldValidateInteractive(false)
      setPhase('question')
    }
  }, [
    currentQuestionIndex,
    sessionQuestions.length,
    onClearPendingLevelUp,
    pendingLevelUp,
  ])

  // ── Answer processing ──────────────────────────────────────
  const processAnswer = useCallback(
    (
      isCorrect: boolean,
      distractorIndex: number,
      errorType?: string,
      delta?: number,
      payload?: string,
    ) => {
      if (answerProcessedRef.current) return
      if (!currentQuestion) return
      answerProcessedRef.current = true
      const timeMs = Date.now() - questionStartTimeRef.current
      feedback.setStreakMilestone(null)
      feedback.setDailyGoalReached(false)

      // Daily Challenge Progress
      const todayChallenge = getTodayChallenge(state, MODULES, selectedGroup)
      let countsTowardsChallenge = false
      if (todayChallenge.type === 'snelheid') {
        countsTowardsChallenge = true
      } else if (todayChallenge.type === 'mix') {
        countsTowardsChallenge = true
      } else if (todayChallenge.moduleId) {
        countsTowardsChallenge =
          isCorrect && currentQuestion.module === todayChallenge.moduleId
      }
      if (countsTowardsChallenge) {
        updateDailyChallengeProgress(
          1,
          todayChallenge.type,
          todayChallenge.targetCount,
        )
      }

      if (isFluency) {
        const questionType = currentQuestion.questionType || 'multiple_choice'
        if (isCorrect && isFastResponse(questionType, timeMs)) {
          feedback.setSpeedStreak((prev) => prev + 1)
        } else {
          feedback.setSpeedStreak(0)
        }
      }

      if (isChallenge) {
        if (!isCorrect) {
          challengeConsecutiveErrorsRef.current++
          if (
            challengeConsecutiveErrorsRef.current >= 3 &&
            !challengeTierDowngraded
          ) {
            setChallengeTierDowngraded(true)
            clearTimeout(challengeTimerRef.current)
            challengeTimerRef.current = setTimeout(
              () => setChallengeTierDowngraded(false),
              SESSION_CONFIG.CHALLENGE_TIER_DOWNGRADE_MS,
            )
          }
        } else {
          challengeConsecutiveErrorsRef.current = 0
        }
      }

      if (!isCorrect) {
        feedback.consecutiveErrorsRef.current++
        if (
          feedback.consecutiveErrorsRef.current >= 3 &&
          !feedback.nudgeFiredRef.current &&
          showCheatSheet
        ) {
          feedback.setCheatSheetNudge(true)
          feedback.nudgeFiredRef.current = true
        }
      } else {
        feedback.consecutiveErrorsRef.current = 0
      }

      // ── P-12: Per-family frustration detection ──────────────
      const frustratedFamilyId = currentQuestion.familyId
      if (frustratedFamilyId && !isCrossModule) {
        if (!isCorrect) {
          const streak =
            (familyErrorStreaksRef.current.get(frustratedFamilyId) || 0) + 1
          familyErrorStreaksRef.current.set(frustratedFamilyId, streak)
          if (streak >= V11_CONFIG.FRUSTRATION_CONSECUTIVE_ERRORS) {
            const futureIndices: number[] = []
            for (
              let fi = currentQuestionIndex + 1;
              fi < sessionQuestions.length;
              fi++
            ) {
              if (sessionQuestions[fi].familyId === frustratedFamilyId) {
                futureIndices.push(fi)
              }
            }
            if (futureIndices.length > 0) {
              const usedIds = new Set(sessionQuestions.map((q) => q.id))
              const allModuleQuestions = moduleEligiblePool.filter(
                (q) => q.familyId !== frustratedFamilyId && !usedIds.has(q.id),
              )
              const familyCounts = new Map<string, number>()
              for (const sq of sessionQuestions) {
                if (sq.familyId) {
                  familyCounts.set(
                    sq.familyId,
                    (familyCounts.get(sq.familyId) || 0) + 1,
                  )
                }
              }
              const replacementPool = allModuleQuestions.filter((q) => {
                const count = familyCounts.get(q.familyId!) || 0
                return count < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY
              })
              const updated = [...sessionQuestions]
              let replacedCount = 0
              for (const idx of futureIndices) {
                if (replacementPool.length > 0) {
                  const replacement = replacementPool.shift()!
                  updated[idx] = replacement
                  usedIds.add(replacement.id)
                  familyCounts.set(
                    replacement.familyId!,
                    (familyCounts.get(replacement.familyId!) || 0) + 1,
                  )
                  replacedCount++
                }
              }
              if (replacedCount > 0) {
                setSessionQuestions(updated)
                debug(
                  `[FrustrationDetection] Family ${frustratedFamilyId}: ${streak} consecutive errors → swapped ${replacedCount}/${futureIndices.length} remaining questions`,
                )
              }
            }
          }
        } else {
          familyErrorStreaksRef.current.set(frustratedFamilyId, 0)
        }
      }

      // ── P-13: Mid-session difficulty adjustment ──────────────
      const diffWindow = V11_CONFIG.DIFFICULTY_ADJUST_WINDOW
      const updatedAnswers = [
        ...sessionAnswers,
        {
          questionId: currentQuestion.id,
          isCorrect,
          familyId: currentQuestion.familyId,
          questionType: currentQuestion.questionType || 'multiple_choice',
        },
      ]
      if (
        !isCrossModule &&
        updatedAnswers.length >= diffWindow &&
        currentQuestionIndex - lastDifficultyAdjustIndexRef.current >=
          V11_CONFIG.DIFFICULTY_ADJUST_COOLDOWN
      ) {
        const recentAnswers = updatedAnswers.slice(-diffWindow)
        const accuracy =
          recentAnswers.filter((a) => a.isCorrect).length / diffWindow
        const upThreshold = V11_CONFIG.DIFFICULTY_ADJUST_UP_THRESHOLD
        const downThreshold = V11_CONFIG.DIFFICULTY_ADJUST_DOWN_THRESHOLD
        let tierShiftDirection: 'up' | 'down' | null = null
        if (upThreshold > 0 && accuracy >= upThreshold) {
          tierShiftDirection = 'up'
        } else if (downThreshold > 0 && accuracy <= downThreshold) {
          tierShiftDirection = 'down'
        }
        if (tierShiftDirection) {
          const shift =
            V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT *
            (tierShiftDirection === 'up' ? 1 : -1)
          const usedIds = new Set(sessionQuestions.map((q) => q.id))
          const allModuleQuestions = moduleEligiblePool.filter(
            (q) => !usedIds.has(q.id),
          )
          const updated = [...sessionQuestions]
          let swappedCount = 0
          for (let fi = currentQuestionIndex + 1; fi < updated.length; fi++) {
            const orig = updated[fi]
            const targetTier = orig.tier + shift
            const replacementIdx = allModuleQuestions.findIndex(
              (q) => q.tier === targetTier && !usedIds.has(q.id),
            )
            if (replacementIdx !== -1) {
              const replacement = allModuleQuestions.splice(
                replacementIdx,
                1,
              )[0]
              updated[fi] = replacement
              usedIds.add(replacement.id)
              swappedCount++
            }
          }
          if (swappedCount > 0) {
            setSessionQuestions(updated)
            lastDifficultyAdjustIndexRef.current = currentQuestionIndex
            debug(
              `[DifficultyAdjust] accuracy=${(accuracy * 100).toFixed(0)}% over last ${diffWindow} → shifted ${tierShiftDirection} by ${V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT} tier(s), swapped ${swappedCount} upcoming questions`,
            )
          }
        }
      }

      const earned = calculateXP(isCorrect, currentQuestion.tier)
      feedback.setXpGained(earned)
      feedback.setTotalXpGained((prev) => prev + earned)
      const questionsAnsweredToday =
        (state.dailyStreak?.todayQuestionsAnswered || 0) + 1
      const dailyGoal =
        state.dailyStreak?.dailyGoalTarget || SESSION_CONFIG.DAILY_GOAL
      if (questionsAnsweredToday === dailyGoal)
        feedback.setDailyGoalReached(true)
      if (isCorrect) {
        const newStreak = feedback.currentStreak + 1
        feedback.setCurrentStreak(newStreak)
        const message = streakMessage(newStreak)
        if (message)
          feedback.setStreakMilestone({
            count: newStreak,
            message,
          })
      } else {
        feedback.setCurrentStreak(0)
      }
      feedback.setMotivationalText(
        motivational({
          isCorrect,
          streak: isCorrect ? feedback.currentStreak + 1 : 0,
          questionNumber: currentQuestionIndex + 1,
          totalQuestions: sessionQuestions.length,
        }),
      )
      setUserAnswerIndex(distractorIndex)
      onAnswer(
        currentQuestion.id,
        isCorrect,
        distractorIndex,
        payload,
        errorType,
        delta,
        timeMs,
        helpUsedForCurrentQuestionRef.current,
      )
      setSessionAnswers((prev) => [
        ...prev,
        {
          questionId: currentQuestion.id,
          isCorrect,
          familyId: currentQuestion.familyId,
          questionType: currentQuestion.questionType || 'multiple_choice',
          errorType,
          timeMs,
        },
      ])
      setSelectedOption(null)
      if (isFluency && isCorrect) {
        setPhase('fluency-correct')
        autoAdvanceTimerRef.current = setTimeout(
          () => nextQuestion(),
          SESSION_CONFIG.FLUENCY_AUTO_ADVANCE_MS,
        )
      } else if (isFluency && !isCorrect) {
        setPhase('fluency-incorrect')
      } else {
        setPhase('feedback')
      }
    },
    [
      currentQuestion,
      currentQuestionIndex,
      sessionQuestions,
      sessionAnswers,
      moduleEligiblePool,
      isFluency,
      isChallenge,
      isCrossModule,
      showCheatSheet,
      challengeTierDowngraded,
      state,
      selectedGroup,
      updateDailyChallengeProgress,
      onAnswer,
      feedback,
      motivational,
      streakMessage,
      nextQuestion,
    ],
  )

  const handleInteractiveComplete = useCallback(
    (userAnswer: string) => {
      if (!currentQuestion) return
      setUserTypedAnswer(userAnswer)
      const config = getQuestionTypeConfig(
        currentQuestion.questionType || 'multiple_choice',
      )
      const validation = config.validateAnswer(userAnswer, currentQuestion)
      if (validation.displayAnswer) setUserTypedAnswer(validation.displayAnswer)
      setActualIsCorrect(validation.isCorrect)
      processAnswer(
        validation.isCorrect,
        validation.distractorIndex ?? 0,
        validation.errorType,
        validation.delta,
        userAnswer,
      )
    },
    [currentQuestion, processAnswer],
  )

  const handleRendererAnswer = useCallback(
    (userAnswer: string | number) => {
      if (isInteractiveQuestion(currentQuestion))
        handleInteractiveComplete(userAnswer as string)
      else {
        setUserAnswerIndex(userAnswer as number)
        setSelectedOption(userAnswer as number)
      }
    },
    [currentQuestion, handleInteractiveComplete],
  )

  const submitAnswer = useCallback(() => {
    if (!currentQuestion) return
    if (isInteractiveQuestion(currentQuestion)) {
      setShouldValidateInteractive(true)
      return
    }
    if (userAnswerIndex === null) return
    processAnswer(
      userAnswerIndex === currentQuestion.correctAnswer,
      userAnswerIndex,
    )
  }, [currentQuestion, userAnswerIndex, processAnswer])

  // Keep ref in sync with latest submitAnswer
  submitAnswerRef.current = submitAnswer

  // ── Enter key handler (F-QA-4 focus guard — PRESERVED) ─────
  useEffect(() => {
    if (!currentQuestion) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return
      if (e.key !== 'Enter') return
      const active = document.activeElement as HTMLElement | null
      if (active) {
        const tag = active.tagName
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          active.isContentEditable === true
        ) {
          return
        }
      }
      const interactive = isInteractiveQuestion(currentQuestion)
      if (interactive && interactiveReady) submitAnswerRef.current()
      else if (!interactive && userAnswerIndex !== null)
        submitAnswerRef.current()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    phase,
    isCheatSheetOpen,
    userAnswerIndex,
    interactiveReady,
    currentQuestion,
  ])

  // ── Skip flow ──────────────────────────────────────────────
  // Skip eligibility WITHOUT the 20 s time gate. The orchestrator ANDs this
  // with useSessionTimer's `canSkipTimePassed` to reproduce the original
  // full gate (phase + 20s + cheatsheet-opened + skip-count + !fluency).
  const canShowSkipBase =
    phase === 'question' &&
    cheatSheetEverOpened &&
    skipCount < MAX_SKIPS_PER_SESSION &&
    !isFluency &&
    currentQuestion !== undefined

  const requestSkip = useCallback((eligible: boolean) => {
    if (!eligible) return
    setShowSkipConfirm(true)
  }, [])

  const handleSkipConfirm = useCallback(() => {
    if (answerProcessedRef.current || !currentQuestion) return
    answerProcessedRef.current = true
    setShowSkipConfirm(false)
    const timeMs = Date.now() - questionStartTimeRef.current
    const skippedFamilyId = currentQuestion.familyId
    onAnswer(
      currentQuestion.id,
      false,
      -1,
      undefined,
      'skipped',
      undefined,
      timeMs,
      helpUsedForCurrentQuestionRef.current,
      true,
    )
    setSessionAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        isCorrect: false,
        familyId: currentQuestion.familyId,
        questionType: currentQuestion.questionType || 'multiple_choice',
        errorType: 'skipped',
        timeMs,
      },
    ])
    feedback.setCurrentStreak(0)
    setSkipCount((c) => c + 1)
    // Focus sessions carry an explicitly selected question set (for example,
    // homework target families). They have no declared replacement pool, so
    // never widen their scope with module-wide candidates.
    if (skippedFamilyId && !isCrossModule) {
      const futureIndices: number[] = []
      for (
        let fi = currentQuestionIndex + 1;
        fi < sessionQuestions.length;
        fi++
      ) {
        if (sessionQuestions[fi].familyId === skippedFamilyId) {
          futureIndices.push(fi)
        }
      }
      if (futureIndices.length > 0) {
        const usedIds = new Set(sessionQuestions.map((q) => q.id))
        const familyCounts = new Map<string, number>()
        for (const sq of sessionQuestions) {
          if (sq.familyId) {
            familyCounts.set(
              sq.familyId,
              (familyCounts.get(sq.familyId) || 0) + 1,
            )
          }
        }
        const replacementPool = moduleEligiblePool.filter((q) => {
          if (q.familyId === skippedFamilyId) return false
          if (usedIds.has(q.id)) return false
          const count = familyCounts.get(q.familyId!) || 0
          return count < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY
        })
        const updated = [...sessionQuestions]
        let replacedCount = 0
        for (const idx of futureIndices) {
          if (replacementPool.length > 0) {
            const replacement = replacementPool.shift()!
            updated[idx] = replacement
            usedIds.add(replacement.id)
            familyCounts.set(
              replacement.familyId!,
              (familyCounts.get(replacement.familyId!) || 0) + 1,
            )
            replacedCount++
          }
        }
        if (replacedCount > 0) {
          setSessionQuestions(updated)
          debug(
            `[Skip] Family ${skippedFamilyId} skipped → swapped ${replacedCount}/${futureIndices.length} remaining questions`,
          )
        }
      }
    }
    setSkipFlashVisible(true)
    clearTimeout(skipFlashTimerRef.current)
    skipFlashTimerRef.current = setTimeout(() => {
      setSkipFlashVisible(false)
      nextQuestion()
    }, SKIP_FLASH_DURATION_MS)
  }, [
    currentQuestion,
    currentQuestionIndex,
    sessionQuestions,
    moduleEligiblePool,
    isCrossModule,
    onAnswer,
    feedback,
    nextQuestion,
  ])

  // Renderer failures are learner-neutral, but must still be explicit session
  // outcomes. Advancing without this record made summaries and persistence
  // disagree about which questions were actually completed.
  const handleRendererFailure = useCallback(() => {
    if (answerProcessedRef.current || !currentQuestion) return
    answerProcessedRef.current = true

    const timeMs = Date.now() - questionStartTimeRef.current
    onAnswer(
      currentQuestion.id,
      false,
      -1,
      undefined,
      'renderer_error',
      undefined,
      timeMs,
      helpUsedForCurrentQuestionRef.current,
      true,
    )
    setSessionAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        isCorrect: false,
        familyId: currentQuestion.familyId,
        questionType: currentQuestion.questionType || 'multiple_choice',
        errorType: 'renderer_error',
        timeMs,
      },
    ])
    feedback.setCurrentStreak(0)
    debug(
      `[RendererFailure] Question ${currentQuestion.id} recorded as skipped after renderer recovery`,
    )
    nextQuestion()
  }, [currentQuestion, onAnswer, feedback, nextQuestion])

  return {
    sessionQuestions,
    currentQuestionIndex,
    currentQuestion,
    isLastQuestion,
    phase,
    sessionInitialized,
    initError,
    preFamilyMastery,
    sessionAnswers,
    selectedOption,
    userAnswerIndex,
    userTypedAnswer,
    actualIsCorrect,
    interactiveReady,
    shouldValidateInteractive,
    setInteractiveReady,
    setPhase,
    challengeTierDowngraded,
    helpUsedForCurrentQuestionRef,
    questionStartTimeRef,
    handleRendererAnswer,
    submitAnswer,
    nextQuestion,
    resetSession,
    skipCount,
    showSkipConfirm,
    setShowSkipConfirm,
    cheatSheetEverOpened,
    setCheatSheetEverOpened,
    canShowSkipBase,
    skipFlashVisible,
    requestSkip,
    handleSkipConfirm,
    handleRendererFailure,
    isInteractiveQuestion,
  }
}
